import React, { useState, useEffect, useCallback } from 'react';
import { useAILearning } from '../hooks/useAILearning';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import AIRecommendations from '../components/AIRecommendations';
import AIPerformanceReport from '../components/AIPerformanceReport';
import AIStudyPlan from '../components/AIStudyPlan';
import AIContentGeneration from '../components/AIContentGeneration';
import { useUserPerformanceSummary } from '../hooks/useUserPerformanceSummary';
import { renderTextWithLinks } from '../utils/formatUtils';
import { AIService } from '../utils/aiService';
import { 
  LightBulbIcon, 
  ChartBarIcon, 
  CalendarIcon, 
  ChatBubbleLeftRightIcon,
  PlusIcon,
  CogIcon,
  SparklesIcon,
  AcademicCapIcon,
  BookOpenIcon,
  PlayIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const AILearningDashboard: React.FC = () => {
  const { currentUser: user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'recommendations' | 'performance' | 'study-plans' | 'content' | 'learning-paths'>('overview');
  const [showGenerateStudyPlan, setShowGenerateStudyPlan] = useState(false);
  const [showGenerateContent, setShowGenerateContent] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [contentGenerating, setContentGenerating] = useState(false);
  
  // Enhanced form states for detailed AI inputs
  const [studyPlanForm, setStudyPlanForm] = useState({
    courseTitle: '',
    courseLevel: 'beginner',
    studyDuration: '2 hours daily',
    learningGoals: '',
    preferredSubjects: '',
    examDate: '',
    currentStrengths: '',
    areasToImprove: '',
    studyStyle: 'visual',
    availableTime: 'weekdays'
  });
  
  const [contentForm, setContentForm] = useState({
    courseTitle: '',
    lessonTopic: '',
    contentType: 'quiz',
    difficulty: 'intermediate',
    targetAudience: 'students',
    specificTopics: '',
    learningObjectives: '',
    prerequisites: '',
    estimatedDuration: '30 minutes'
  });
  const [aiServiceStatus, setAiServiceStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
  const [aiFeatures, setAiFeatures] = useState<string[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);

  // Initialize AI Service
  const aiService = AIService.getInstance();

  // AI Learning Hook
  const {
    recommendations,
    studyPlans,
    loading: aiLearningLoading,
    error: aiLearningError,
    generateRecommendations,
    generatePerformanceReport,
    generateStudyPlan,
    generateContent,
    markRecommendationAsRead,
    updateStudyPlan,
    getLatestPerformanceReport,
    getUnreadRecommendationsCount,
    clearError: clearAILearningError
  } = useAILearning(user?.uid || '');

  const { messages: chatHistory, isTyping, error: chatbotError, clearError: clearChatbotError, openChat } = useChat();

  // Real user performance summary from Firestore
  const { summary: userPerformance, reload: reloadPerformance } = useUserPerformanceSummary(user?.uid);

  // Check AI service status on mount
  useEffect(() => {
    const checkAIService = async () => {
      try {
        setAiServiceStatus('checking');
        
        // Test AI service with a simple prompt
        const testResponse = await aiService.generateRecommendations(user?.uid || 'test', {
          recentPerformance: { overallScore: 75, strengths: ['math'], weaknesses: ['accounting'] },
          learningGoals: ['improve accounting', 'master math concepts'],
          studyTime: '2 hours daily'
        });

        if (testResponse && testResponse.length > 0) {
          setAiServiceStatus('available');
          setAiFeatures(['recommendations', 'study-plans', 'content-generation', 'chatbot']);
        } else {
          setAiServiceStatus('unavailable');
        }
      } catch (error) {
        console.warn('AI service check failed:', error);
        setAiServiceStatus('unavailable');
      }
    };

    checkAIService();
  }, [user?.uid, aiService]);

  // Generate initial data on component mount
  useEffect(() => {
    if (user?.uid && aiServiceStatus === 'available') {
      // Trigger recommendations when performance summary is loaded
      if (userPerformance) {
        generateRecommendations(userPerformance);
      }
      generatePerformanceReport('course_1', 'weekly');
    }
  }, [user?.uid, userPerformance, generateRecommendations, generatePerformanceReport, aiServiceStatus]);

  const handleGenerateStudyPlan = useCallback(async () => {
    try {
      setContentGenerating(true);
      
      const userSchedule = {
        availableDays: [1, 3, 5], // Monday, Wednesday, Friday
        preferredTime: '18:00',
        studyDuration: '2 hours'
      };
      
      const learningGoals = [
        'Complete course modules on time',
        'Improve understanding of core concepts',
        'Practice with real-world examples',
        'Prepare for upcoming assessments'
      ];

      const userContext = {
        currentCourses: ['ICAG Fundamentals', 'CITG Basics'],
        learningStyle: 'visual and practical',
        preferredFormat: 'interactive exercises and real examples'
      };

      const studyPlan = await generateStudyPlan('course_1', userSchedule, learningGoals);
      
      if (studyPlan) {
        setShowGenerateStudyPlan(false);
        // Refresh study plans
        // This would typically be handled by the hook
      }
    } catch (error) {
      console.error('Failed to generate study plan:', error);
    } finally {
      setContentGenerating(false);
    }
  }, [generateStudyPlan]);

  const handleGenerateContent = useCallback(async (type: 'quiz' | 'summary' | 'flashcards' | 'practice_exercises' | 'likely_questions') => {
    try {
      setContentGenerating(true);
      setShowGenerateContent(false);
      
      // Enhanced lesson content for better AI generation
      const lessonContent = `This comprehensive lesson covers fundamental ${type} principles in professional accounting and taxation. Students will learn through:

Key Concepts:
- Core theoretical frameworks
- Practical applications in Ghanaian context
- Real-world case studies and examples
- Industry best practices

Learning Objectives:
- Understand fundamental principles
- Apply concepts to practical scenarios
- Develop critical thinking skills
- Prepare for professional examinations

Assessment Methods:
- Interactive quizzes and exercises
- Case study analysis
- Practical problem-solving
- Self-assessment tools`;

      const content = await generateContent(
        type as any,
        'course_1',
        'lesson_1',
        lessonContent
      );
      
      if (content) {
        setGeneratedContent(content);
      } else {
        setGeneratedContent(null);
      }
    } catch (error) {
      console.error('Failed to generate content:', error);
      setGeneratedContent(null);
    } finally {
      setContentGenerating(false);
    }
  }, [generateContent]);

  const handleViewRecommendation = useCallback((recommendation: any) => {
    // Navigate to the recommended item or show details
    console.log('Viewing recommendation:', recommendation);
  }, []);

  // Enhanced form handlers
  const handleStudyPlanFormChange = useCallback((field: string, value: string) => {
    setStudyPlanForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleContentFormChange = useCallback((field: string, value: string) => {
    setContentForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Enhanced AI generation with detailed context
  const handleGenerateStudyPlanWithDetails = useCallback(async () => {
    if (!user?.uid) return;
    
    setContentGenerating(true);
    try {
      const enhancedPrompt = `Create a comprehensive study plan for a student with the following details:
        
        Course Information:
        - Course Title: ${studyPlanForm.courseTitle}
        - Course Level: ${studyPlanForm.courseLevel}
        - Study Duration: ${studyPlanForm.studyDuration}
        
        Learning Goals: ${studyPlanForm.learningGoals}
        Preferred Subjects: ${studyPlanForm.preferredSubjects}
        Exam Date: ${studyPlanForm.examDate}
        
        Student Profile:
        - Current Strengths: ${studyPlanForm.currentStrengths}
        - Areas to Improve: ${studyPlanForm.areasToImprove}
        - Study Style: ${studyPlanForm.studyStyle}
        - Available Time: ${studyPlanForm.availableTime}
        
        Please create a detailed, personalized study plan that includes:
        1. Weekly study schedule
        2. Topic breakdown and timeline
        3. Study techniques for their learning style
        4. Practice exercises and assessments
        5. Progress tracking milestones
        6. Tips for their specific areas of improvement`;

      const response = await aiService.generateStudyPlan(user.uid, 'enhanced-plan', { availableDays: [0,1,2,3,4,5,6], preferredTime: '09:00' }, [studyPlanForm.learningGoals]);
      if (response) {
        // Handle the enhanced study plan response
        console.log('Enhanced study plan generated:', response);
        setShowSuccessMessage('Study plan generated successfully! Check the Study Plans tab to view it.');
        setTimeout(() => setShowSuccessMessage(null), 5000);
        // You can store this in your study plans or display it
      }
    } catch (error) {
      console.error('Error generating enhanced study plan:', error);
    } finally {
      setContentGenerating(false);
      setShowGenerateStudyPlan(false);
    }
  }, [user?.uid, studyPlanForm, aiService]);

  const handleGenerateContentWithDetails = useCallback(async (contentType: string) => {
    if (!user?.uid) return;
    
    setContentGenerating(true);
    try {
      const enhancedPrompt = `Generate ${contentType} content for a lesson with the following details:
        
        Course Information:
        - Course Title: ${contentForm.courseTitle}
        - Lesson Topic: ${contentForm.lessonTopic}
        - Content Type: ${contentType}
        - Difficulty Level: ${contentForm.difficulty}
        
        Target Audience: ${contentForm.targetAudience}
        Specific Topics: ${contentForm.specificTopics}
        Learning Objectives: ${contentForm.learningObjectives}
        Prerequisites: ${contentForm.prerequisites}
        Estimated Duration: ${contentForm.estimatedDuration}
        
        Please create comprehensive ${contentType} content that:
        1. Matches the specified difficulty level
        2. Covers the specific topics mentioned
        3. Achieves the learning objectives
        4. Is appropriate for the target audience
        5. Can be completed within the estimated duration
        6. Includes clear instructions and examples`;

      const response = await aiService.generateContent(
        'course_1', // courseId
        'lesson_1', // lessonId
        contentType as 'quiz' | 'summary' | 'flashcards' | 'practice_exercises' | 'likely_questions', // type
        enhancedPrompt, // lessonContent
        5 // contentLength
      );
      if (response) {
        setGeneratedContent({ ...response, type: contentType });
        setShowSuccessMessage(`${contentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} content generated successfully!`);
        setTimeout(() => setShowSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error generating enhanced content:', error);
    } finally {
      setContentGenerating(false);
      setShowGenerateContent(false);
    }
  }, [user?.uid, contentForm, aiService]);

  const renderAIServiceStatus = () => {
    if (aiServiceStatus === 'checking') {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-blue-800 text-sm">Checking AI service availability...</span>
          </div>
        </div>
      );
    }

    if (aiServiceStatus === 'unavailable') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CogIcon className="h-5 w-5 text-yellow-600 mr-3" />
            <div>
              <span className="text-yellow-800 text-sm font-medium">AI Service Unavailable</span>
              <p className="text-yellow-700 text-xs mt-1">
                Some AI features may not work. Check your API configuration or try again later.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <SparklesIcon className="h-5 w-5 text-green-600 mr-3" />
          <div>
            <span className="text-green-800 text-sm font-medium">AI Service Available</span>
            <p className="text-green-700 text-xs mt-1">
              All AI features are working. You can generate study plans, content, and get personalized recommendations.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderOverview = () => (
    <div className="space-y-4 lg:space-y-6">
      {/* AI Service Status */}
      {renderAIServiceStatus()}

      <div className="grid gap-3 lg:gap-6 grid-cols-2 lg:grid-cols-4">
        {/* AI Recommendations Count */}
        <div className="bg-white p-3 lg:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <LightBulbIcon className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">AI Recommendations</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{getUnreadRecommendationsCount()}</p>
            </div>
          </div>
        </div>

        {/* Performance Score */}
        <div className="bg-white p-3 lg:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <ChartBarIcon className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Performance Score</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">
                {getLatestPerformanceReport()?.overallScore || 'N/A'}%
              </p>
            </div>
          </div>
        </div>

        {/* Active Study Plans */}
        <div className="bg-white p-3 lg:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CalendarIcon className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Active Study Plans</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">
                {studyPlans.filter(plan => plan.isActive).length}
              </p>
            </div>
          </div>
        </div>

        {/* Empty placeholder for grid balance if needed, or just remove */}
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-4">Quick AI Actions</h3>
        <div className="grid gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-3">
          <button
            onClick={() => setShowGenerateStudyPlan(true)}
            disabled={aiServiceStatus !== 'available'}
            className="p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AcademicCapIcon className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-xs lg:text-sm font-medium text-gray-700">Generate Study Plan</p>
          </button>
          
          <button
            onClick={() => setShowGenerateContent(true)}
            disabled={aiServiceStatus !== 'available'}
            className="p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BookOpenIcon className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-xs lg:text-sm font-medium text-gray-700">Generate Content</p>
          </button>
          
          <button
            onClick={() => openChat({ fullScreen: true })}
            disabled={aiServiceStatus !== 'available'}
            className="p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChatBubbleLeftRightIcon className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-xs lg:text-sm font-medium text-gray-700">Chat with AI</p>
          </button>
        </div>
      </div>

      {/* Learning Paths Progress */}
      <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-4 flex items-center">
          <AcademicCapIcon className="w-5 h-5 mr-2 text-indigo-600" />
          Learning Paths Progress
        </h3>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">ICAG Fundamentals</span>
              <span className="text-sm text-gray-600">65%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full" style={{ width: '65%' }}></div>
            </div>
            <p className="text-xs text-gray-500">Next: Financial Accounting Module</p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">CITG Basics</span>
              <span className="text-sm text-gray-600">25%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '25%' }}></div>
            </div>
            <p className="text-xs text-gray-500">Next: Tax Fundamentals Module</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => setActiveTab('learning-paths')}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            View All Learning Paths →
          </button>
        </div>
      </div>

      {/* AI Features Status */}
      {aiServiceStatus === 'available' && (
        <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-4 flex items-center">
            <RocketLaunchIcon className="w-5 h-5 mr-2 text-green-600" />
            Available AI Features
          </h3>
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-4">
            {aiFeatures.map((feature) => (
              <div key={feature} className="flex items-center space-x-2 p-2 bg-green-50 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-green-800 capitalize">
                  {feature.replace('-', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-4">Recent AI Activity</h3>
        <div className="space-y-3">
          {recommendations.slice(0, 3).map((rec) => (
            <div key={rec.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <LightBulbIcon className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-900 truncate">{rec.title}</p>
                <p className="text-xs text-gray-500 truncate">{rec.reason}</p>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {new Date(rec.createdAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'recommendations':
        return (
          <AIRecommendations
            recommendations={recommendations}
            onMarkAsRead={markRecommendationAsRead}
            onViewRecommendation={handleViewRecommendation}
          />
        );
      case 'performance':
        return getLatestPerformanceReport() ? (
          <AIPerformanceReport
            report={getLatestPerformanceReport()!}
            onGenerateNewReport={() => generatePerformanceReport('course_1', 'weekly')}
          />
        ) : (
          <div className="text-center py-8 lg:py-12">
            <ChartBarIcon className="w-12 h-12 lg:w-16 lg:h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-sm lg:text-base text-gray-500">No performance reports available</p>
            <button
              onClick={() => generatePerformanceReport('course_1', 'weekly')}
              className="mt-4 px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm lg:text-base"
            >
              Generate Report
            </button>
          </div>
        );
      case 'study-plans':
        return (
          <div className="space-y-4 lg:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <h3 className="text-base lg:text-lg font-medium text-gray-900">AI Study Plans</h3>
              <button
                onClick={() => setShowGenerateStudyPlan(true)}
                className="px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center text-sm lg:text-base"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Generate New Plan
              </button>
            </div>
            
            {studyPlans.length === 0 ? (
              <div className="text-center py-8 lg:py-12">
                <CalendarIcon className="w-12 h-12 lg:w-16 lg:h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm lg:text-base text-gray-500">No study plans available</p>
                <button
                  onClick={() => setShowGenerateStudyPlan(true)}
                  className="mt-4 px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm lg:text-base"
                >
                  Create Your First Plan
                </button>
              </div>
            ) : (
              studyPlans.map((plan) => (
                <AIStudyPlan
                  key={plan.id}
                  studyPlan={plan}
                  onUpdatePlan={updateStudyPlan}
                />
              ))
            )}
          </div>
        );
      case 'content':
        return (
          <div className="space-y-4 lg:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <h3 className="text-base lg:text-lg font-medium text-gray-900">AI-Generated Content</h3>
              <button
                onClick={() => setShowGenerateContent(true)}
                className="px-3 py-2 lg:px-4 lg:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center text-sm lg:text-base"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Generate Content
              </button>
            </div>
            
            {contentGenerating ? (
              <div className="text-center py-8 lg:py-12">
                <div className="animate-spin rounded-full h-12 w-12 lg:h-16 lg:w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-sm lg:text-base text-gray-500">AI is generating your content...</p>
                <p className="text-xs lg:text-sm text-gray-400 mt-2">This may take a few moments</p>
              </div>
            ) : generatedContent ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm lg:text-base font-medium text-gray-700">
                    Latest Generated Content: {generatedContent.type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </h4>
                  <button
                    onClick={() => setGeneratedContent(null)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
                <AIContentGeneration
                  content={generatedContent}
                  onGenerateNewContent={handleGenerateContent}
                />
              </div>
            ) : (
              <div className="text-center py-8 lg:py-12">
                <LightBulbIcon className="w-12 h-12 lg:w-16 lg:h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm lg:text-base text-gray-500">No AI-generated content available yet</p>
                <p className="text-xs lg:text-sm text-gray-400 mt-2">
                  Generate quizzes, summaries, flashcards, and practice exercises
                </p>
              </div>
            )}
          </div>
        );
      case 'learning-paths':
        return (
          <div className="space-y-4 lg:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <h3 className="text-base lg:text-lg font-medium text-gray-900">AI Learning Paths</h3>
              <button
                onClick={() => {
                  // Generate a new learning path based on user performance
                  if (userPerformance) {
                    generateRecommendations(userPerformance);
                  }
                }}
                className="px-3 py-2 lg:px-4 lg:py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center text-sm lg:text-base"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Generate New Path
              </button>
            </div>
            
            {/* Learning Paths Overview */}
            <div className="grid gap-4 lg:gap-6 grid-cols-1 lg:grid-cols-2">
              {/* ICAG Fundamentals Path */}
              <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base lg:text-lg font-medium text-gray-900">ICAG Fundamentals</h4>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Progress</span>
                    <span className="text-sm font-medium text-gray-900">65%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Next milestone: Complete Module 3</p>
                    <p className="text-xs text-gray-500 mt-1">Estimated: 2 weeks</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Current Module</span>
                    <span className="font-medium text-gray-900">Financial Accounting</span>
                  </div>
                </div>
              </div>

              {/* CITG Basics Path */}
              <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base lg:text-lg font-medium text-gray-900">CITG Basics</h4>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Planning</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Progress</span>
                    <span className="text-sm font-medium text-gray-900">25%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '25%' }}></div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Next milestone: Start Module 2</p>
                    <p className="text-xs text-gray-500 mt-1">Estimated: 1 week</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Current Module</span>
                    <span className="font-medium text-gray-900">Tax Fundamentals</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Learning Path Recommendations */}
            <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
              <h4 className="text-base lg:text-lg font-medium text-gray-900 mb-4">Recommended Learning Paths</h4>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <div className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer">
                  <h5 className="font-medium text-gray-900 text-sm">Advanced Accounting</h5>
                  <p className="text-xs text-gray-600 mt-1">Master complex accounting concepts</p>
                  <div className="flex items-center mt-2">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">6 months</span>
                    <span className="text-xs text-gray-500 ml-auto">High Priority</span>
                  </div>
                </div>
                
                <div className="p-3 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors cursor-pointer">
                  <h5 className="font-medium text-gray-900 text-sm">Business Law</h5>
                  <p className="text-xs text-gray-600 mt-1">Understand legal frameworks</p>
                  <div className="flex items-center mt-2">
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">4 months</span>
                    <span className="text-xs text-gray-500 ml-auto">Medium Priority</span>
                  </div>
                </div>
                
                <div className="p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors cursor-pointer">
                  <h5 className="font-medium text-gray-900 text-sm">Audit & Assurance</h5>
                  <p className="text-xs text-gray-600 mt-1">Learn audit procedures</p>
                  <div className="flex items-center mt-2">
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">8 months</span>
                    <span className="text-xs text-gray-500 ml-auto">Low Priority</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            {/* Left - Text */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <SparklesIcon className="h-3 w-3 mr-1.5 text-purple-300" />
                AI Learning
              </div>
              
              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">AI Learning Dashboard</span>
                <span className="block bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 bg-clip-text text-transparent">
                  Personalized & Smart
                </span>
              </h1>
              
              {/* Subtitle */}
              <p className="text-base sm:text-lg text-purple-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Generate study plans and content, get recommendations, and chat with Profstudymate.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => setActiveTab('study-plans')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <AcademicCapIcon className="h-4 w-4 mr-1.5" /> Study Plans
                </button>
              </div>
            </div>
            
            {/* Right - Visuals (desktop only) */}
            <div className="relative hidden xl:block">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                      <LightBulbIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Smart Insights</h3>
                      <p className="text-purple-200 text-sm">AI-powered recommendations</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full flex items-center justify-center">
                      <RocketLaunchIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Fast Actions</h3>
                      <p className="text-purple-200 text-sm">Generate content quickly</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6">
        {/* Navigation Tabs */}
        <div className="mb-4 lg:mb-6">
          <nav className="flex flex-wrap gap-1 bg-white p-1 rounded-lg shadow-sm border border-gray-200">
            {[
              { id: 'overview', label: 'Overview', icon: ChartBarIcon },
              { id: 'recommendations', label: 'Recommendations', icon: LightBulbIcon },
              { id: 'performance', label: 'Performance', icon: ChartBarIcon },
              { id: 'study-plans', label: 'Study Plans', icon: CalendarIcon },
              { id: 'content', label: 'AI Content', icon: LightBulbIcon },
              { id: 'learning-paths', label: 'Learning Paths', icon: AcademicCapIcon }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-xs lg:text-sm font-medium rounded-md transition-colors flex-1 sm:flex-none ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-3 h-3 lg:w-4 lg:h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Error Display */}
        {(aiLearningError || chatbotError) && (
          <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm lg:text-base text-red-600">
                {aiLearningError || chatbotError}
              </span>
              <button
                onClick={() => {
                  clearAILearningError();
                  clearChatbotError();
                }}
                className="text-red-400 hover:text-red-600 text-lg"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Success Message Display */}
        {showSuccessMessage && (
          <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                </div>
                <span className="text-sm lg:text-base text-green-800">
                  {showSuccessMessage}
                </span>
              </div>
              <button
                onClick={() => setShowSuccessMessage(null)}
                className="text-green-400 hover:text-green-600 text-lg"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {aiLearningLoading && (
          <div className="text-center py-8 lg:py-12">
            <div className="animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm lg:text-base text-gray-500 mt-4">AI is analyzing your learning data...</p>
          </div>
        )}

        {/* Main Content */}
        {!aiLearningLoading && renderTabContent()}


        {/* Generate Study Plan Modal */}
        {showGenerateStudyPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 lg:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-4">Generate AI Study Plan</h3>
              <p className="text-sm lg:text-base text-gray-600 mb-6">
                Provide detailed information to help AI create a personalized study plan for you.
              </p>
              
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course Title</label>
                  <input
                    type="text"
                    value={studyPlanForm.courseTitle}
                    onChange={(e) => handleStudyPlanFormChange('courseTitle', e.target.value)}
                    placeholder="e.g., ICAG Fundamentals"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course Level</label>
                  <select
                    value={studyPlanForm.courseLevel}
                    onChange={(e) => handleStudyPlanFormChange('courseLevel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Study Duration</label>
                  <select
                    value={studyPlanForm.studyDuration}
                    onChange={(e) => handleStudyPlanFormChange('studyDuration', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1 hour daily">1 hour daily</option>
                    <option value="2 hours daily">2 hours daily</option>
                    <option value="3 hours daily">3 hours daily</option>
                    <option value="4 hours daily">4 hours daily</option>
                    <option value="weekends only">Weekends only</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Available Time</label>
                  <select
                    value={studyPlanForm.availableTime}
                    onChange={(e) => handleStudyPlanFormChange('availableTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="weekdays">Weekdays</option>
                    <option value="weekends">Weekends</option>
                    <option value="evenings">Evenings</option>
                    <option value="mornings">Mornings</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Learning Goals</label>
                  <textarea
                    value={studyPlanForm.learningGoals}
                    onChange={(e) => handleStudyPlanFormChange('learningGoals', e.target.value)}
                    placeholder="What do you want to achieve? (e.g., Master accounting fundamentals, Prepare for ICAG exam)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Subjects</label>
                  <input
                    type="text"
                    value={studyPlanForm.preferredSubjects}
                    onChange={(e) => handleStudyPlanFormChange('preferredSubjects', e.target.value)}
                    placeholder="e.g., Accounting, Taxation, Business Law"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Exam Date (Optional)</label>
                  <input
                    type="date"
                    value={studyPlanForm.examDate}
                    onChange={(e) => handleStudyPlanFormChange('examDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Study Style</label>
                  <select
                    value={studyPlanForm.studyStyle}
                    onChange={(e) => handleStudyPlanFormChange('studyStyle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="visual">Visual</option>
                    <option value="auditory">Auditory</option>
                    <option value="kinesthetic">Kinesthetic</option>
                    <option value="reading">Reading/Writing</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Strengths</label>
                  <textarea
                    value={studyPlanForm.currentStrengths}
                    onChange={(e) => handleStudyPlanFormChange('currentStrengths', e.target.value)}
                    placeholder="What subjects or topics are you already good at?"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Areas to Improve</label>
                  <textarea
                    value={studyPlanForm.areasToImprove}
                    onChange={(e) => handleStudyPlanFormChange('areasToImprove', e.target.value)}
                    placeholder="What subjects or topics do you need help with?"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mt-6">
                <button
                  onClick={handleGenerateStudyPlanWithDetails}
                  disabled={contentGenerating || !studyPlanForm.courseTitle || !studyPlanForm.learningGoals}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm lg:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {contentGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <AcademicCapIcon className="w-4 h-4 mr-2" />
                      Generate Study Plan
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowGenerateStudyPlan(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors text-sm lg:text-base"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generate Content Modal */}
        {showGenerateContent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 lg:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-4">Generate AI Content</h3>
              <p className="text-sm lg:text-base text-gray-600 mb-6">
                Provide details to help AI generate personalized learning content for you.
              </p>
              
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course Title</label>
                  <input
                    type="text"
                    value={contentForm.courseTitle}
                    onChange={(e) => handleContentFormChange('courseTitle', e.target.value)}
                    placeholder="e.g., ICAG Fundamentals"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Topic</label>
                  <input
                    type="text"
                    value={contentForm.lessonTopic}
                    onChange={(e) => handleContentFormChange('lessonTopic', e.target.value)}
                    placeholder="e.g., Double Entry Bookkeeping"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                  <select
                    value={contentForm.contentType}
                    onChange={(e) => handleContentFormChange('contentType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="quiz">Quiz</option>
                    <option value="summary">Summary</option>
                    <option value="flashcards">Flashcards</option>
                    <option value="practice_exercises">Practice Exercises</option>
                    <option value="likely_questions">Likely Exam Questions</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level</label>
                  <select
                    value={contentForm.difficulty}
                    onChange={(e) => handleContentFormChange('difficulty', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                  <select
                    value={contentForm.targetAudience}
                    onChange={(e) => handleContentFormChange('targetAudience', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="students">Students</option>
                    <option value="professionals">Professionals</option>
                    <option value="beginners">Beginners</option>
                    <option value="advanced">Advanced Learners</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Duration</label>
                  <select
                    value={contentForm.estimatedDuration}
                    onChange={(e) => handleContentFormChange('estimatedDuration', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="15 minutes">15 minutes</option>
                    <option value="30 minutes">30 minutes</option>
                    <option value="45 minutes">45 minutes</option>
                    <option value="1 hour">1 hour</option>
                    <option value="2 hours">2 hours</option>
                  </select>
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specific Topics</label>
                  <input
                    type="text"
                    value={contentForm.specificTopics}
                    onChange={(e) => handleContentFormChange('specificTopics', e.target.value)}
                    placeholder="e.g., Journal entries, Ledger accounts, Trial balance"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Learning Objectives</label>
                  <textarea
                    value={contentForm.learningObjectives}
                    onChange={(e) => handleContentFormChange('learningObjectives', e.target.value)}
                    placeholder="What should students learn from this content?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prerequisites</label>
                  <textarea
                    value={contentForm.prerequisites}
                    onChange={(e) => handleContentFormChange('prerequisites', e.target.value)}
                    placeholder="What knowledge should students have before this content?"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mt-6">
                <button
                  onClick={() => handleGenerateContentWithDetails(contentForm.contentType)}
                  disabled={contentGenerating || !contentForm.courseTitle || !contentForm.lessonTopic}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors text-sm lg:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {contentGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <BookOpenIcon className="w-4 h-4 mr-2" />
                      Generate Content
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowGenerateContent(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors text-sm lg:text-base"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generated Content Display */}
        {generatedContent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 lg:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base lg:text-lg font-medium text-gray-900">
                  Generated {generatedContent.type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} Content
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      // Export content as JSON
                      const dataStr = JSON.stringify(generatedContent, null, 2);
                      const dataBlob = new Blob([dataStr], { type: 'application/json' });
                      const url = URL.createObjectURL(dataBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `ai-content-${generatedContent.type}-${new Date().toISOString().split('T')[0]}.json`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                  >
                    Export JSON
                  </button>
                  <button
                    onClick={() => {
                      // Export content as text
                      let textContent = '';
                      if (generatedContent.type === 'quiz') {
                        textContent = generatedContent.questions?.map((q: any, i: number) => 
                          `${i + 1}. ${q.question}\nOptions: ${q.options?.join(', ')}\nCorrect: ${q.correctAnswer}\n\n`
                        ).join('') || '';
                      } else if (generatedContent.type === 'summary') {
                        textContent = generatedContent.summary || '';
                      } else if (generatedContent.type === 'flashcards') {
                        textContent = generatedContent.flashcards?.map((f: any, i: number) => 
                          `Card ${i + 1}:\nFront: ${f.front}\nBack: ${f.back}\n\n`
                        ).join('') || '';
                      }
                      
                      const dataBlob = new Blob([textContent], { type: 'text/plain' });
                      const url = URL.createObjectURL(dataBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `ai-content-${generatedContent.type}-${new Date().toISOString().split('T')[0]}.txt`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Export Text
                  </button>
                  <button
                    onClick={() => setGeneratedContent(null)}
                    className="text-gray-400 hover:text-gray-600 text-lg"
                  >
                    ×
                  </button>
                </div>
              </div>
              <AIContentGeneration
                content={generatedContent}
                onGenerateNewContent={handleGenerateContent}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AILearningDashboard;
